import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  X, ChevronLeft, ChevronRight, Check, 
  FileText, Briefcase, Target, Sparkles,
  TrendingUp, Search, Award, HelpCircle, MessageSquare,
  // Advanced tour icons
  FileCheck, DollarSign, Mail, Layout, BookOpen, Users,
  UserPlus, ClipboardList, Wand2, Mic, BarChart3, Wifi, Crown,
  GraduationCap, Rocket
} from 'lucide-react';

/**
 * Welcome Tour Component
 * Interactive onboarding tour for new users
 * Now with Advanced Tour option for power users
 */

const WelcomeTour = ({ onComplete, onSkip }) => {
  // Check if advanced tour should be launched directly (from Settings)
  const [launchAdvanced] = useState(() => {
    if (typeof window !== 'undefined') {
      const flag = localStorage.getItem('launchAdvancedTour') === 'true';
      if (flag) {
        localStorage.removeItem('launchAdvancedTour');
      }
      return flag;
    }
    return false;
  });
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isAdvancedTour, setIsAdvancedTour] = useState(launchAdvanced);
  const [showAdvancedPrompt, setShowAdvancedPrompt] = useState(false);

  // Basic Tour steps configuration (8 steps)
  const basicTourSteps = [
    {
      title: '👋 Welcome to CareerSupport!',
      description: 'Your all-in-one career development platform. Let\'s take a quick tour to get you started!',
      icon: Sparkles,
      color: 'from-blue-500 to-purple-500',
      image: null,
      target: null
    },
    {
      title: '📄 Build Your Resume',
      description: 'Create professional, ATS-optimized resumes with our AI-powered builder. Choose from multiple templates and let AI help you write compelling content.',
      icon: FileText,
      color: 'from-sky-500 to-blue-500',
      features: [
        'AI-powered content suggestions',
        'ATS optimization built-in',
        'Multiple professional templates',
        'Easy editing and updates'
      ],
      target: 'resume-builder'
    },
    {
      title: '🔍 Search & Track Jobs',
      description: 'Search thousands of jobs from multiple sources and track your applications all in one place.',
      icon: Search,
      color: 'from-green-500 to-emerald-500',
      features: [
        'Search from 100,000+ jobs',
        'Save interesting opportunities',
        'Track application status',
        'Set up job alerts'
      ],
      target: 'job-search'
    },
    {
      title: '✨ Career Assessments',
      description: 'Discover your strengths, interests, and ideal career paths with our comprehensive assessment tools.',
      icon: Target,
      color: 'from-purple-500 to-pink-500',
      features: [
        'Skills assessment',
        'Career interest tests',
        'Personality analysis',
        'Personalized recommendations'
      ],
      target: 'assessments'
    },
    {
      title: '💼 Interview Preparation',
      description: 'Practice with AI-powered mock interviews and get ready to ace your next interview.',
      icon: Briefcase,
      color: 'from-orange-500 to-red-500',
      features: [
        'AI mock interviews',
        'Role-specific questions',
        'Feedback and scoring',
        'Interviewing tips'
      ],
      target: 'interview-prep'
    },
    {
      title: '🎯 Career Planning',
      description: 'Create personalized career plans with actionable steps and track your progress toward your goals.',
      icon: TrendingUp,
      color: 'from-indigo-500 to-purple-500',
      features: [
        'Set career goals',
        'Create action plans',
        'Track progress',
        'Get skill recommendations'
      ],
      target: 'career-planner'
    },
    {
      title: '🏆 Career Boost Finder',
      description: 'Discover credentials, certifications, licenses, internships, apprenticeships, and other career-boosting opportunities to advance your career.',
      icon: Award,
      color: 'from-amber-500 to-orange-500',
      features: [
        'Search certifications & licenses',
        'Find internships & apprenticeships',
        'Get AI-powered recommendations',
        'Save opportunities for later'
      ],
      target: 'career-boost-finder'
    },
    {
      title: '🚀 Ready to Get Started!',
      description: 'You\'ve completed the basics! Go to the Dashboard to start exploring, or take the Advanced Tour to discover power-user features like AI Assistant, Help Center, and more.',
      icon: Check,
      color: 'from-green-500 to-emerald-500',
      showAdvancedOption: true,
      target: null
    }
  ];

  // Advanced Tour steps configuration (15 steps)
  const advancedTourSteps = [
    {
      title: '🎓 Welcome to the Advanced Tour!',
      description: 'Discover powerful tools and features that will take your career search to the next level. These advanced features help you stand out from the competition.',
      icon: GraduationCap,
      color: 'from-purple-600 to-indigo-600',
      features: [
        'Advanced resume optimization',
        'Salary insights & negotiation tools',
        'Professional networking opportunities',
        'Premium career resources'
      ],
      target: null
    },
    {
      title: '❓ Help Center',
      description: 'Need assistance? Our Help Center has everything you need to get the most out of Career Support.',
      icon: HelpCircle,
      color: 'from-cyan-500 to-blue-500',
      features: [
        'Browse articles and guides for detailed help',
        'FAQ page for quick answers to common questions',
        'Contact Support at the bottom left of any page'
      ],
      target: 'help-center'
    },
    {
      title: '🤖 AI Assistant',
      description: 'Meet your Career Support AI Assistant! Available on every page to help you succeed.',
      icon: MessageSquare,
      color: 'from-violet-500 to-purple-500',
      features: [
        'Career planning guidance',
        'Resume writing assistance',
        'Job search tips',
        'Located in the bottom right corner of any page'
      ],
      target: 'ai-assistant'
    },
    {
      title: '📊 ATS Resume Check',
      description: 'Understand how Applicant Tracking Systems (ATS) work and optimize your resume to pass automated screening.',
      icon: FileCheck,
      color: 'from-blue-500 to-cyan-500',
      features: [
        'Scan your resume against job descriptions',
        'Get an ATS compatibility score',
        'Receive keyword recommendations',
        'Improve your match rate significantly'
      ],
      target: 'ats-check'
    },
    {
      title: '💰 Salary Analyzer',
      description: 'Make informed career decisions with comprehensive salary data for your target roles and locations.',
      icon: DollarSign,
      color: 'from-green-500 to-emerald-500',
      features: [
        'Compare salaries by role & experience',
        'View location-based compensation data',
        'Prepare for salary negotiations',
        'Plan your career advancement path'
      ],
      target: 'salary-analyzer'
    },
    {
      title: '✉️ Cover Letter Generator',
      description: 'Create compelling, personalized cover letters in seconds with our AI-powered generator.',
      icon: Mail,
      color: 'from-pink-500 to-rose-500',
      features: [
        'AI-generated role-specific letters',
        'Tailor letters to each job posting',
        'Save hours of writing time',
        'Professional tone and formatting'
      ],
      target: 'cover-letter'
    },
    {
      title: '🎨 Browse Resume Templates',
      description: 'Choose from our collection of modern, ATS-friendly templates designed for various industries.',
      icon: Layout,
      color: 'from-violet-500 to-purple-500',
      features: [
        'Modern, professional designs',
        'ATS-optimized formatting',
        'Industry-specific templates',
        'Easy one-click customization'
      ],
      target: 'browse-templates'
    },
    {
      title: '📚 Career Resources Hub',
      description: 'Access a comprehensive library of tools, guides, and resources for workforce development.',
      icon: BookOpen,
      color: 'from-amber-500 to-orange-500',
      features: [
        'External tools and learning platforms',
        'Job board aggregators',
        'Certification resources',
        'Workforce development guides'
      ],
      target: 'career-resources'
    },
    {
      title: '🤝 Affiliate Partner Program',
      description: 'Earn rewards by sharing CareerSupport with others. Perfect for coaches, influencers, and community advocates.',
      icon: UserPlus,
      color: 'from-teal-500 to-cyan-500',
      features: [
        'Earn commissions on referrals',
        'Track your earnings in real-time',
        'Access marketing materials',
        'Ideal for career coaches & influencers'
      ],
      target: 'affiliate-program'
    },
    {
      title: '👨‍🏫 Coaches Marketplace',
      description: 'Connect with experienced career coaches for personalized guidance and accountability.',
      icon: Users,
      color: 'from-indigo-500 to-blue-500',
      features: [
        'Browse certified career coaches',
        'Resume review & interview coaching',
        'Career transition support',
        '1:1 personalized guidance'
      ],
      target: 'coaches-marketplace'
    },
    {
      title: '📋 Job Tracking Tool',
      description: 'Stay organized during your job search with our comprehensive application tracking system.',
      icon: ClipboardList,
      color: 'from-sky-500 to-blue-500',
      features: [
        'Track all your applications',
        'Set follow-up reminders',
        'Monitor interview stages',
        'Never miss an opportunity'
      ],
      target: 'job-tracking'
    },
    {
      title: '🎯 AI Resume Tailoring',
      description: 'Generate custom resume variants for each job application to maximize your match rate.',
      icon: Wand2,
      color: 'from-purple-500 to-pink-500',
      features: [
        'Create job-specific resume versions',
        'AI analyzes job descriptions',
        'Automatic keyword optimization',
        'Improve application success rate'
      ],
      target: 'ai-tailoring'
    },
    {
      title: '🎤 Interview Prep Tools',
      description: 'Practice with AI-powered mock interviews and build confidence before the real thing.',
      icon: Mic,
      color: 'from-red-500 to-orange-500',
      features: [
        'AI mock interview sessions',
        'Role-specific question banks',
        'Instant feedback & scoring',
        'Practice until you\'re confident'
      ],
      target: 'interview-tools'
    },
    {
      title: '📈 Career Assessments',
      description: 'Discover your ideal career path with comprehensive assessments and personalized insights.',
      icon: BarChart3,
      color: 'from-emerald-500 to-teal-500',
      features: [
        'Skills & strengths assessment',
        'Interest & personality analysis',
        'Career path recommendations',
        'Identify growth opportunities'
      ],
      target: 'career-assessments'
    },
    {
      title: '🌐 Remote Work Tools',
      description: 'Find and filter remote opportunities with our specialized work-from-home job tools.',
      icon: Wifi,
      color: 'from-blue-500 to-indigo-500',
      features: [
        'Remote job discovery',
        'Work-from-home filters',
        'Hybrid opportunity search',
        'Location-flexible positions'
      ],
      target: 'remote-work'
    },
    {
      title: '👑 Premium Features',
      description: 'Unlock the full potential of CareerSupport with premium access and coach-exclusive tools.',
      icon: Crown,
      color: 'from-amber-500 to-yellow-500',
      features: [
        'Unlimited AI-powered features',
        'Priority support access',
        'Advanced analytics & insights',
        'Exclusive coach marketplace access'
      ],
      target: 'premium'
    },
    {
      title: '🎉 You\'re a Power User Now!',
      description: 'Congratulations! You\'ve explored all of CareerSupport\'s advanced features. You\'re ready to supercharge your career journey!',
      icon: Rocket,
      color: 'from-gradient-500 to-purple-600',
      features: [
        'All features at your fingertips',
        'AI assistant always available',
        'Career resources ready to explore',
        'Start your journey to success!'
      ],
      cta: 'Go to Dashboard',
      target: null
    }
  ];

  const tourSteps = isAdvancedTour ? advancedTourSteps : basicTourSteps;
  const currentStepData = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  useEffect(() => {
    // Check if tours have been shown
    const tourShown = localStorage.getItem('tourShown');
    const advancedTourShown = localStorage.getItem('advancedTourShown');
    
    // If starting advanced tour directly
    if (isAdvancedTour && advancedTourShown) {
      // Allow resuming - do nothing
    } else if (tourShown && !isAdvancedTour) {
      // Hide tour if already shown
      requestAnimationFrame(() => {
        setIsVisible(false);
      });
    }
  }, [isAdvancedTour]);

  const handleNext = () => {
    if (isLastStep) {
      if (isAdvancedTour) {
        handleAdvancedComplete();
      } else if (currentStepData.showAdvancedOption) {
        // Start the Advanced Tour directly (no prompt modal)
        handleStartAdvancedTour();
      } else {
        handleComplete();
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (isAdvancedTour) {
      localStorage.setItem('advancedTourShown', 'true');
    } else {
      localStorage.setItem('tourShown', 'true');
    }
    setIsVisible(false);
    if (onSkip) onSkip();
  };

  const handleComplete = () => {
    localStorage.setItem('tourShown', 'true');
    localStorage.setItem('tourCompleted', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleAdvancedComplete = () => {
    localStorage.setItem('advancedTourShown', 'true');
    localStorage.setItem('advancedTourCompleted', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleStartAdvancedTour = () => {
    localStorage.setItem('tourShown', 'true');
    localStorage.setItem('tourCompleted', 'true');
    setIsAdvancedTour(true);
    setCurrentStep(0);
    setShowAdvancedPrompt(false);
  };

  const handleSkipAdvancedTour = () => {
    handleComplete();
  };

  if (!isVisible) return null;

  const IconComponent = currentStepData.icon;

  // Advanced Tour Prompt Modal
  if (showAdvancedPrompt) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8">
            {/* Icon */}
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Rocket className="w-12 h-12 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
              Want to Become a Power User? 🚀
            </h2>
            
            <p className="text-gray-600 text-center mb-6">
              You&apos;ve learned the basics! The <strong>Advanced Tour</strong> covers 13 powerful features that will give you an edge in your career search.
            </p>
            
            <div className="bg-purple-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-purple-800 font-medium mb-2">You&apos;ll discover:</p>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• ATS optimization & resume grading</li>
                <li>• Salary analyzer & negotiation tools</li>
                <li>• AI resume tailoring per job</li>
                <li>• Coaches marketplace & affiliate program</li>
                <li>• And much more...</li>
              </ul>
            </div>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleStartAdvancedTour}
                size="lg"
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
              >
                <GraduationCap className="w-5 h-5 mr-2" />
                Take the Advanced Tour
              </Button>
              
              <Button
                onClick={handleSkipAdvancedTour}
                variant="outline"
                size="lg"
                className="w-full border-2"
              >
                <Check className="w-5 h-5 mr-2" />
                Get Started (Skip Advanced Tour)
              </Button>
            </div>
            
            <p className="text-xs text-gray-400 text-center mt-4">
              You can always access the Advanced Tour later from Settings
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Tour type indicator */}
        {isAdvancedTour && (
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-center py-1 text-xs font-medium">
            ✨ Advanced Tour
          </div>
        )}
        
        {/* Progress bar */}
        <div className={`absolute ${isAdvancedTour ? 'top-6' : 'top-0'} left-0 right-0 h-1 bg-gray-200`}>
          <div 
            className={`h-full transition-all duration-300 ${
              isAdvancedTour 
                ? 'bg-gradient-to-r from-purple-500 to-indigo-500' 
                : 'bg-gradient-to-r from-blue-500 to-purple-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close button */}
        <button
          onClick={handleSkip}
          className={`absolute ${isAdvancedTour ? 'top-10' : 'top-4'} right-4 p-2 rounded-full hover:bg-gray-100 transition-colors`}
          aria-label="Skip tour"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Content */}
        <div className={`p-8 ${isAdvancedTour ? 'pt-14' : 'pt-12'}`}>
          {/* Icon and title */}
          <div className="text-center mb-6">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${currentStepData.color} flex items-center justify-center shadow-lg`}>
              <IconComponent className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {currentStepData.title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {currentStepData.description}
            </p>
          </div>

          {/* Features list */}
          {currentStepData.features && (
            <div className={`rounded-xl p-6 mb-6 ${isAdvancedTour ? 'bg-purple-50' : 'bg-gray-50'}`}>
              <ul className="space-y-3">
                {currentStepData.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${isAdvancedTour ? 'text-purple-500' : 'text-green-500'}`} />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Step indicator */}
          <div className="flex justify-center items-center gap-2 mb-6">
            {tourSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? `w-8 ${isAdvancedTour ? 'bg-purple-500' : 'bg-blue-500'}` 
                    : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {/* Skip Tour - Always visible as a button */}
            <Button
              onClick={handleSkip}
              variant="outline"
              className="border-2 border-gray-300 text-gray-600 hover:bg-gray-100"
            >
              Skip Tour
            </Button>

            {/* Back button - visible when not on first step */}
            {!isFirstStep && (
              <Button
                onClick={handlePrev}
                variant="outline"
                className="border-2"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}

            {/* Final step of basic tour: Show Take Advanced Tour + Go to Dashboard */}
            {isLastStep && !isAdvancedTour && currentStepData.showAdvancedOption ? (
              <>
                <Button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white"
                >
                  <GraduationCap className="w-4 h-4 mr-1" />
                  Advanced Tour
                </Button>
                <Button
                  onClick={handleComplete}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                >
                  Go to Dashboard
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            ) : (
              /* Normal Next/Finish button for other steps */
              <Button
                onClick={handleNext}
                size="lg"
                className={`min-w-[120px] ${
                  isAdvancedTour 
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white'
                    : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white'
                }`}
              >
                {isLastStep ? (
                  <>
                    {currentStepData.cta || 'Finish'}
                    <Check className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Step counter */}
          <div className="text-center mt-4 text-sm text-gray-500">
            Step {currentStep + 1} of {tourSteps.length}
            {isAdvancedTour && <span className="ml-2 text-purple-500">(Advanced)</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook to check if user should see the tour
 */
export const useShouldShowTour = () => {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const tourShown = localStorage.getItem('tourShown');
    const isNewUser = !localStorage.getItem('lastVisit');
    
    if (!tourShown && isNewUser) {
      // Delay showing tour slightly for better UX
      setTimeout(() => setShouldShow(true), 1000);
    }
    
    localStorage.setItem('lastVisit', new Date().toISOString());
  }, []);

  return shouldShow;
};

/**
 * Button to restart tour
 */
export const RestartTourButton = ({ children, variant = 'basic', ...props }) => {
  const handleRestart = () => {
    if (variant === 'advanced') {
      localStorage.removeItem('advancedTourShown');
      localStorage.removeItem('advancedTourCompleted');
    } else {
      localStorage.removeItem('tourShown');
      localStorage.removeItem('tourCompleted');
    }
    window.location.reload();
  };

  return (
    <Button onClick={handleRestart} {...props}>
      {children || (variant === 'advanced' ? 'Take Advanced Tour' : 'Take Tour Again')}
    </Button>
  );
};

/**
 * Component to launch Advanced Tour directly
 */
export const StartAdvancedTourButton = ({ children, ...props }) => {
  const handleStart = () => {
    localStorage.removeItem('advancedTourShown');
    localStorage.removeItem('advancedTourCompleted');
    localStorage.setItem('launchAdvancedTour', 'true');
    window.location.reload();
  };

  return (
    <Button onClick={handleStart} {...props}>
      {children || 'Take Advanced Tour'}
    </Button>
  );
};

export default WelcomeTour;
